import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  BookOpen,
  Sparkles,
  Quote,
  ExternalLink,
} from "lucide-react";
import { api } from "@/lib/api";
import { Sunflower } from "@/components/site/Sunflower";
import { getPricing, formatUSD } from "@/lib/pricing";

const formatPrice = (price, currency) =>
  `${formatUSD(price)} (${(currency || "usd").toUpperCase()})`;

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(`/products/${id}`);
        setProduct(data);
      } catch {
        navigate("/shop");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, navigate]);

  if (loading) {
    return (
      <div className="min-h-[60vh] grid place-items-center text-brand-muted">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  if (!product) return null;

  return (
    <div data-testid="product-detail-page" className="section-pad relative overflow-hidden">
      <div className="absolute -top-10 -right-16 opacity-20 pointer-events-none hidden md:block">
        <Sunflower size={240} className="animate-slowspin" />
      </div>

      <Link
        data-testid="product-back-link"
        to="/shop"
        className="inline-flex items-center gap-2 text-sm text-brand-muted hover:text-brand-sunDeep mb-10 font-body"
      >
        <ArrowLeft size={16} /> Back to shop
      </Link>

      <div className="relative grid lg:grid-cols-12 gap-14">
        <div className="lg:col-span-6">
          <div className="relative">
            <div className="rounded-[32px] overflow-hidden border-4 border-brand-sun/40 bg-gradient-to-br from-brand-honey/30 to-brand-leafSoft/15 shadow-earthyLg aspect-[4/5] grid place-items-center p-8">
              <img
                src={product.image}
                alt={`${product.title} book cover`}
                className="max-h-full max-w-full object-contain drop-shadow-[0_18px_40px_rgba(44,36,22,0.22)]"
              />
            </div>
            {product.is_new && (
              <div
                data-testid="product-new-badge"
                className="absolute -top-3 right-6 bg-brand-rust text-brand-cream text-xs uppercase tracking-[0.22em] font-bold px-4 py-2 rounded-full shadow-earthy flex items-center gap-2"
              >
                <Sparkles size={14} /> Just released
              </div>
            )}
          </div>

          {/* Quick facts */}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {product.pages && (
              <div className="bg-white rounded-2xl border-2 border-brand-line p-4 text-center">
                <p className="font-brand text-2xl text-brand-sunDeep">
                  {product.pages}
                </p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1 font-body">
                  Pages
                </p>
              </div>
            )}
            {product.age_group && (
              <div className="bg-white rounded-2xl border-2 border-brand-line p-4 text-center">
                <p className="font-heading text-base text-brand-leafDeep mt-1">
                  {product.age_group}
                </p>
                <p className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1 font-body">
                  Audience
                </p>
              </div>
            )}
            <div className="bg-white rounded-2xl border-2 border-brand-line p-4 text-center">
              <p className="font-heading text-base text-brand-leafDeep mt-1">
                Instant PDF
              </p>
              <p className="text-[11px] uppercase tracking-[0.18em] text-brand-muted mt-1 font-body">
                Delivery
              </p>
            </div>
          </div>
        </div>

        <div className="lg:col-span-6">
          <span className="inline-flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-brand-leafDeep bg-brand-honey/50 border border-brand-sun/40 rounded-full px-4 py-1.5 mb-5 w-fit">
            <BookOpen size={14} /> {product.category}
          </span>
          <h1 className="font-heading font-normal text-4xl md:text-5xl leading-[1.05] tracking-tight">
            {product.title}
          </h1>
          {product.subtitle && (
            <p className="mt-3 text-brand-muted italic text-lg font-body">
              {product.subtitle}
            </p>
          )}

          {product.synopsis && (
            <div className="mt-7 bg-brand-ivory rounded-2xl border-2 border-brand-line p-6">
              <p className="text-xs uppercase tracking-[0.22em] text-brand-leafDeep font-semibold mb-3 inline-flex items-center gap-2">
                <Sparkles size={14} /> Synopsis
              </p>
              <p className="text-brand-ink leading-relaxed font-body text-[15px]">
                {product.synopsis}
              </p>
            </div>
          )}

          {product.moral && (
            <div className="mt-5 relative bg-gradient-to-br from-brand-sun/20 to-brand-honey/30 rounded-2xl border-2 border-brand-sun/40 p-6">
              <Quote
                className="absolute -top-3 -left-3 text-brand-sunDeep bg-brand-cream rounded-full p-1.5"
                size={28}
              />
              <p className="text-xs uppercase tracking-[0.22em] text-brand-leafDeep font-semibold mb-2">
                Moral / Key Message
              </p>
              <p className="text-brand-ink italic leading-relaxed font-heading text-lg">
                "{product.moral}"
              </p>
            </div>
          )}

          <div className="mt-7 grid gap-6 text-sm font-body">
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-brand-leafDeep font-semibold mb-1.5">
                The problem it solves
              </p>
              <p className="text-brand-ink leading-relaxed">{product.problem}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-brand-leafDeep font-semibold mb-1.5">
                Who it's for
              </p>
              <p className="text-brand-muted leading-relaxed">{product.who_for}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-brand-leafDeep font-semibold mb-3">
                What's included
              </p>
              <ul className="grid gap-2">
                {product.includes.map((i) => (
                  <li key={i} className="flex items-start gap-3 text-brand-ink">
                    <CheckCircle2
                      size={16}
                      className="text-brand-sunDeep shrink-0 mt-0.5"
                    />
                    {i}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.22em] text-brand-leafDeep font-semibold mb-3">
                Benefits
              </p>
              <ul className="grid gap-2">
                {product.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-3 text-brand-ink">
                    <CheckCircle2
                      size={16}
                      className="text-brand-leafDeep shrink-0 mt-0.5"
                    />
                    {b}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-10 bg-gradient-to-br from-brand-sun/30 via-brand-honey/40 to-brand-leafSoft/25 rounded-[28px] p-8 border-2 border-brand-sun/40">
            {(() => {
              const pricing = getPricing(product.price);
              const currency = (product.currency || "usd").toUpperCase();
              const payable = pricing.hasDiscount ? pricing.sale : pricing.original;
              return (
                <>
                  <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
                    <span className="text-sm text-brand-muted font-body">
                      Instant PDF delivery via Gumroad
                    </span>
                    {pricing.hasDiscount ? (
                      <div data-testid="product-detail-price" className="flex flex-col items-end">
                        <div className="flex items-baseline gap-3">
                          <span className="font-brand text-3xl text-brand-leafDeep">
                            {formatUSD(pricing.sale)}
                          </span>
                          <span className="text-base text-brand-muted line-through font-body">
                            {formatUSD(pricing.original)}
                          </span>
                          <span className="text-[10px] uppercase tracking-[0.18em] text-brand-muted font-body">
                            {currency}
                          </span>
                        </div>
                        <div className="mt-1.5 flex items-center gap-2">
                          <span className="inline-flex items-center text-[10px] uppercase tracking-[0.22em] font-bold text-brand-honey bg-brand-leafDeep rounded-full px-2.5 py-1">
                            −{pricing.percentOff}% OFF
                          </span>
                          <span
                            data-testid="product-detail-savings"
                            className="text-xs font-medium text-brand-leafDeep font-body"
                          >
                            You save {formatUSD(pricing.savings)}
                          </span>
                        </div>
                      </div>
                    ) : (
                      <span className="font-brand text-3xl text-brand-leafDeep">
                        {formatPrice(product.price, product.currency)}
                      </span>
                    )}
                  </div>
                  <a
                    data-testid="product-buy-btn"
                    href={product.gumroad_url || "https://zahabina.gumroad.com"}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-full rounded-full bg-brand-ink hover:bg-brand-leafDeep text-brand-cream px-7 py-4 text-sm font-semibold transition-colors inline-flex items-center justify-center gap-2 shadow-earthy"
                  >
                    Buy on Gumroad — {formatPrice(payable, product.currency)}
                    <ExternalLink size={14} />
                  </a>
                  <p className="mt-4 text-xs text-brand-muted text-center font-body">
                    Secure checkout on Gumroad. PDF download arrives instantly after purchase.
                  </p>
                </>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}
